import { Button, CheckIcon, Modal } from '@st/theme'
import type { Folder } from '@st/sdk'
import { match } from '@st/pattern'
import { ClientCard, ClientCardStepHeading } from '~/features/st-questionnaire/ui'
import { useProcess, useSelect } from '~/store'
import { ContractSigningModal } from './contract-signing-modal'
import { selContractCard, type ContractCardSource } from './contract-signing-shell-module'
import { PaymentRequiredNotice } from './payment-required-notice'
import { SignedContractModal } from './signed-contract-modal'

/** Dashboard step for one contract: renders the card model and sends intent. */
export function ClientContractCard({
  stepNumber,
  folder,
  source,
  paymentRequired,
  isArchived
}: {
  stepNumber: number
  folder: Folder
  source: ContractCardSource
  paymentRequired: boolean
  isArchived: boolean
}) {
  const contractSigning = useProcess('contractSigning')
  const card = useSelect('contractSigning', selContractCard, {
    folderId: folder.id,
    source,
    isArchived
  })

  return (
    <ClientCard>
      <ClientCardStepHeading
        stepNumber={stepNumber}
        completed={card.completed}
        title={<h2 className="text-xl font-semibold text-gray-900">{card.title}</h2>}
        status={match(card.completed)
          .with(true, () => null)
          .with(false, () => <div>Incomplete</div>)
          .exhaustive()}
        action={match(card.heading)
          .with(undefined, () => null)
          .with({ type: 'view' }, (action) => (
            <Button
              variant="default"
              size="l"
              className="min-w-24"
              disabled={!action.enabled}
              onClick={() => contractSigning.send(action.message)}
            >
              View
            </Button>
          ))
          .with({ type: 'open' }, (action) => (
            <Button
              variant="primary"
              size="l"
              className="min-w-24"
              disabled={!action.enabled}
              onClick={() => contractSigning.send(action.message)}
            >
              {action.label}
            </Button>
          ))
          .exhaustive()}
      />

      {card.signerRows.length > 0 && (
        <div className="mt-5 divide-y divide-gray-200 border-t border-gray-200">
          {card.signerRows.map(({ signer, roleTitle, action }) => (
            <div key={signer.id} className="flex items-center justify-between gap-4 py-4 last:pb-0">
              <div>
                <div className="font-medium text-gray-900">{signer.displayName}</div>
                <div className="text-sm text-gray-500">{roleTitle}</div>
              </div>
              {match(action)
                .with({ type: 'open' }, (action) => (
                  <Button
                    variant="primary"
                    disabled={!action.enabled}
                    aria-label={`Review and sign as ${signer.displayName}`}
                    onClick={() => contractSigning.send(action.message)}
                  >
                    {action.label}
                  </Button>
                ))
                .with({ type: 'signed' }, () => (
                  <span className="inline-flex min-h-10 items-center gap-1.5 text-sm text-green-600">
                    <CheckIcon className="size-4" />
                    Signed
                  </span>
                ))
                .exhaustive()}
            </div>
          ))}
        </div>
      )}

      {match(card.dialog)
        .with(undefined, () => null)
        .with({ type: 'signing' }, ({ document, signing, paymentPromptOpen }) => (
          <ContractSigningModal
            document={document}
            signing={signing}
            isArchived={isArchived}
            paymentRequired={paymentRequired}
            paymentPromptOpen={paymentPromptOpen}
          />
        ))
        .with({ type: 'paymentRequired' }, () => (
          <Modal
            isOpen={true}
            onClose={() => contractSigning.send({ type: 'paymentRequiredClosed' })}
          >
            <PaymentRequiredNotice
              onClose={() => contractSigning.send({ type: 'paymentRequiredClosed' })}
              onGoToDashboard={() => contractSigning.send({ type: 'paymentRequiredClosed' })}
            />
          </Modal>
        ))
        .with({ type: 'signed' }, ({ document }) => <SignedContractModal document={document} />)
        .exhaustive()}
    </ClientCard>
  )
}
